<template>
  <i
    class="tm-visual-menu"
    :class="{
      // not v-if - cause of fade animation
      v: isRelevant,
    }"
    :style="styleObj"
  >
    <i class="-shadow">
      <i class="-balloon">
        <i
          class="-button"
          title="Text & Effects"
          @mouseup.left.stop="$_menuEditVisual"
        >
          <i class="wticon wticon-pencil3" />
        </i>

        <template v-if="isRelevant && isVideoFragment">
          <i class="-divider" />

          <i
            class="-button"
            title="Re-Trim"
            @mouseup.left.stop="$_menuEditSegment"
          >
            <i class="wticon wticon-Cut2" />
          </i>
        </template>

        <i class="-divider" />
        <i
          class="-button -tune1"
          title="Collage"
          :class="{
            '-disabled': disableCollage,
          }"
          @mouseup.left.stop="$_menuEditScene"
        >
          <i class="wticon wticon-Add-Media8" />
        </i>

        <i class="-divider" />

        <i
          class="-button"
          title="Duplicate"
          @mouseup.left.stop="$_menuDuplicateVisual"
        >
          <i class="wticon wticon-duplicate" />
        </i>

        <i class="-divider" />

        <i
          class="-button"
          title="Delete"
          @mouseup.left.stop="$_menuRemoveVisual"
        >
          <i class="wticon wticon-trash-line" />
        </i>
      </i>
    </i>
  </i>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash-extended';
import tmBus from '../../api/tmBus';

export default {
  name: 'tm-visual-menu',

  props: {
    hoveredRectangle: { type: Object, required: false, default: null },
  },

  data() {
    return {
      lastRectangle: null,
      activeRectangle: null,
    };
  },

  computed: {
    ...mapGetters('timemachine', ['isDndMode']),
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      track: (state) => state.selected.visual,
      cssVariables: (state) => state.cssVariables,
      axis: (state) => state.viewOffsets.axis,
      useSceneEdit: (state) => state.USE.sceneEdit,
    }),

    isRelevant() {
      return this.track && !this.isDndMode && !this.MODE_SCENE_EDIT;
    },

    isVideoFragment() {
      return (
        this.track &&
        (app.assetType.isFRAGMENT(this.track.data || {}) ||
          app.assetType.isSOCIALFRAGMENT(this.track.data || {}))
      );
    },

    disableCollage() {
      return !this.useSceneEdit;
    },

    trackRectangles() {
      return this.track?.rectangles;
    },

    styleObj() {
      if (!this.$data.activeRectangle || this.isDndMode) {
        return;
      }

      const point = this.$data.activeRectangle.p1.clone();
      const rightEdge = this.axis.width - this.cssVariables.pxAxisSidePadding;
      const menuRightX = point.x + this.cssVariables.pxVisualMenuW;

      if (menuRightX > rightEdge) {
        const shiftLeft = menuRightX - rightEdge;
        point.shift(-shiftLeft, 0);
      }

      return {
        transform: `translate3d(${point.x}px, ${point.y}px, 0)`,
      };
    },
  },

  watch: {
    hoveredRectangle(rectangle) {
      if (this.$_trackContains(rectangle)) {
        this.$data.activeRectangle = rectangle;
      } else {
        this.$data.lastRectangle = rectangle;
      }
    },

    trackRectangles: {
      deep: true,
      handler() {
        if (this.track) {
          if (this.$_trackContains(this.$data.lastRectangle)) {
            this.$data.activeRectangle = this.$data.lastRectangle;
          } else {
            this.$data.activeRectangle = _.first(this.track.rectangles);
          }
        } else {
          this.$data.activeRectangle = null;
        }
      },
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'REMOVE_VISUAL',
      'UX_AFTER_REMOVE_VISUAL',
      'DUPLICATE_VISUAL',
      'SCENE_EDIT_MODE_ENTER',
      'SELECT_VISUAL',
      'UNSELECT_TRACKS',
      'SET_MODE',
    ]),

    $_trackContains(rectangle) {
      let rv = false;

      if (this.track && rectangle) {
        rv = this.track.rectangles.indexOf(rectangle) >= 0;
      }

      return rv;
    },

    $_menuEditVisual: _.debounce(
      function (e) {
        tmBus.$emit('callbackEditAsset', {
          asset: this.track.data,
          overlayGuid: null,
        });

        // this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });
        // this.UNSELECT_TRACKS();
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuEditScene: _.debounce(
      function (e) {
        this.SCENE_EDIT_MODE_ENTER({ track: this.track }).then(
          ({ sceneTrack }) => {
            tmBus.$emit('callbackEnterSceneEdit', {
              asset: sceneTrack.data,
              autopilotEntering: false,
            });
          }
        );
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuEditSegment: _.debounce(
      function (e) {
        tmBus.$emit('callbackEditSegment', {
          asset: this.track,
          overlayGuid: null,
        });
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuDuplicateVisual: _.debounce(
      function (e) {
        this.DUPLICATE_VISUAL(this.track).then(({ newTrack }) => {
          tmBus.$emit('callbackChange', { e });
          this.SELECT_VISUAL({ track: newTrack });
        });
      },
      256,
      { leading: true, trailing: false }
    ),

    $_menuRemoveVisual: _.debounce(
      function (e) {
        const data = this.track.data;
        this.REMOVE_VISUAL(this.track).then((res) => {
          this.UX_AFTER_REMOVE_VISUAL({
            removedTrackIndex: res.trackIndex,
            toTheLeft: !e.shiftKey,
          }).then((res) => {
            tmBus.$emit('callbackAssetRemoved', {
              e,
              asset: data,
              seekToTime: res.seekToTime,
            });
          });
        });
      },
      256,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss">
.tm-tracks > .tm-visual-menu {
  --menuHeight: 40px;
  --buttonWidth: 50px;

  position: absolute;
  top: 0;
  z-index: var(--tm-zTrackItemMenu);
  margin-top: -14px;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in;
  white-space: nowrap;
  display: flex;

  &.v {
    visibility: visible;
    opacity: 1;
  }

  > .-shadow {
    box-shadow: -1px 1px 10px $color-black-l;
    display: flex;

    > .-balloon {
      display: flex;
      flex-direction: row;
      align-content: center;
      align-items: center;
      width: var(--tm-pxVisualMenuW);
      height: var(--menuHeight);
      background-color: #fff;
      border: 1px solid #ccc;

      > .-divider {
        border-right: 1px solid $color-black-l;
        height: 100%;
        width: 0;
      }

      > .-button {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        height: calc(var(--menuHeight) - 2px);
        width: var(--buttonWidth);
        color: $color-normal;

        &:hover {
          background-color: $color-black-l2;
        }

        &.-disabled {
          pointer-events: none;
          .wticon {
            color: $color-disabled;
          }
        }

        .wticon {
          font-size: 20px;
        }

        &.-tune1 {
          .wticon {
            transform: translate(0.5px, 1px);
          }
        }
      }
    }
  }
}
</style>
